import React, { PropsWithChildren, useEffect, useState } from 'react';
import styles from './stages.module.scss';

/* Translations */
import { Trans, useTranslation } from 'react-i18next';
import { tr } from '../../../utils/translations/translator';

/* Components */
import { PaymentMethods } from '../components/PaymentMethods/PaymentMethods';

/* Types */
import { StoreType } from '../interfaces/store_types';
import { Product } from '../interfaces/Product';
import { PremiumShop } from '../../Shops/Premium/PremiumShop';
import { TipShop } from '../../Shops/Tip/TipShop';
import { PaidTipProduct } from '../../Shops/Tip/interfaces/TipProduct';
import { PremiumProduct } from '../../Shops/Premium/interfaces/PremiumProduct';
import { Shop } from '../../Shops/shared/interfaces';

/* Providers */
import { useAnalyticsHandlers } from '../../../analytics/__providers__/AnalyticsHandlersProvider';

interface SelectPaymentProps {
  storeType: StoreType;
  product: Product;
  shop: Shop;
}

const TERMS: Record<StoreType, string> = {
  PREMIUM: 'premium_purchase',
  TIP_PURCHASE: 'tip_purchase',
};

export const SelectPayment: React.FC<
  SelectPaymentProps & PropsWithChildren
> = ({ storeType, product, shop }) => {
  const { t } = useTranslation();
  const analytics = useAnalyticsHandlers();
  const [isTracked, setIsTracked] = useState(false);

  useEffect(() => {
    switch (storeType) {
      case 'PREMIUM':
        const premiumShop = shop as PremiumShop;

        if (!isTracked) {
          analytics?.client.Mixpanel.log('WEB_PREMIUM_PAYMENT_METHOD_VIEWED', [
            {
              tipster_id: premiumShop.tipsterInfo.id,
              tipster_name: premiumShop.tipsterInfo.name,
              premium_plan_type: (premiumShop.selectedProduct as PremiumProduct)
                .template.code,
              price_amount: (
                premiumShop.selectedProduct as PremiumProduct
              ).price
                .buildPrice()
                .toNumber(),
              currency: (premiumShop.selectedProduct as PremiumProduct).price
                .currency,
              discount: (premiumShop.selectedProduct as PremiumProduct)
                .discountPercentage,
            },
          ]);
        }
        break;
      case 'TIP_PURCHASE':
        const tipShop = shop as TipShop;

        if (!isTracked) {
          analytics?.client.Mixpanel.log('WEB_TIP_PAYMENT_METHOD_VIEWED', [
            {
              tipster_id: tipShop.tipsterInfo.id,
              tipster_name: tipShop.tipsterInfo.name,
              tip_id: (tipShop.selectedProduct as PaidTipProduct).tip.id,
              price_amount: (tipShop.selectedProduct as PaidTipProduct).price
                .buildPrice()
                .toNumber(),
              currency: (tipShop.selectedProduct as PaidTipProduct).price
                .currency,
              discount: (tipShop.selectedProduct as PaidTipProduct)
                .discountPercentage,
            },
          ]);
        }
        break;
      default:
        break;
    }

    setIsTracked(true);
  }, []);

  return (
    <div className={styles.card}>
      {product.unlockable ? (
        <p>{tr(t, 'you_have_an_available_coupon')}</p>
      ) : (
        <p>{tr(t, 'choose_a_payment_method')}</p>
      )}
      <PaymentMethods store={storeType} product={product} />
      <div className={styles.terms}>
        <Trans
          i18nKey="accepting_the_terms_and_conditions"
          values={{ terms_and_conditions: t('terms_and_conditions') }}
          components={[
            <a
              href={`https://www.tipsterchat.com/terms/${TERMS[storeType]}`}
              target="_blank"
            >
              {t('terms_and_conditions')}
            </a>,
          ]}
        />
      </div>
      {/* {children} */}
    </div>
  );
};
